import React, { useState } from "react";
import { toast } from "react-hot-toast";
import { useHistory } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import API from "../../api";
import { capture } from "../../instrument";
import { INPUT_CLASS_NAME, isValidIp } from "../../utils";

const parseLines = (text) =>
  text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

const AddNetwork = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [originsText, setOriginsText] = useState("");
  const [indexNamesText, setIndexNamesText] = useState("");
  const [allowedIpsText, setAllowedIpsText] = useState("");

  const createMutation = useMutation({
    mutationFn: (body) => API.post("/admin/network", body),
    onSuccess: (response) => {
      if (!response.ok) return toast.error(response.message);
      toast.success("Réseau ajouté avec succès");
      queryClient.invalidateQueries({ queryKey: ["networks"] });
      history.push("/network");
    },
    onError: (error) => {
      capture(error);
      toast.error("Une erreur est survenue");
    },
  });

  const addNetwork = () => {
    if (!name) return toast.error("Veuillez renseigner le nom du réseau");
    const allowedIps = parseLines(allowedIpsText);
    const invalidIp = allowedIps.find((ip) => !isValidIp(ip));
    if (invalidIp) return toast.error(`IP invalide : ${invalidIp}`);
    createMutation.mutate({ name, origins: parseLines(originsText), indexNames: parseLines(indexNamesText), allowedIps });
  };

  return (
    <div className="flex h-[calc(100vh-60px)] flex-col bg-gray-50">
      <div className="flex items-start justify-between border-b border-gray-200 bg-white px-8 pb-5 pt-6">
        <h1 className="text-lg font-semibold text-gray-900">Ajouter un réseau</h1>
        <div className="flex shrink-0 items-center gap-1.5 pl-4">
          <button
            type="button"
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2"
            onClick={() => history.push("/network")}
          >
            Annuler
          </button>
          <button
            type="button"
            className="rounded-lg bg-primary-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
            onClick={addNetwork}
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? "Chargement..." : "Ajouter"}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-4xl px-8 py-6">
          <div className="divide-y divide-gray-100 rounded-lg border border-gray-100 bg-white">
            <div className="flex items-center gap-4 px-5 py-3.5">
              <label className="w-64 shrink-0 text-[13px] font-medium text-gray-600">Nom*</label>
              <input className={INPUT_CLASS_NAME} placeholder="Nom du réseau (ex: B2A)" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="flex items-start gap-4 px-5 py-3.5">
              <div className="w-64 shrink-0 pt-2">
                <span className="text-[13px] font-medium text-gray-600">Origines</span>
                <span className="block text-xs text-gray-500">Une origine par ligne (ex: https://b2a.querco.co)</span>
              </div>
              <textarea
                className={`${INPUT_CLASS_NAME} min-h-[100px] resize-y font-mono text-xs`}
                placeholder={"https://b2a.querco.co\nhttps://admission.b2a.fr"}
                value={originsText}
                onChange={(e) => setOriginsText(e.target.value)}
              />
            </div>
            <div className="flex items-start gap-4 px-5 py-3.5">
              <div className="w-64 shrink-0 pt-2">
                <span className="text-[13px] font-medium text-gray-600">IndexNames</span>
                <span className="block text-xs text-gray-500">Un indexName par ligne</span>
              </div>
              <textarea
                className={`${INPUT_CLASS_NAME} min-h-[100px] resize-y font-mono text-xs`}
                placeholder={"b2avosges\nb2abiorhin\nb2abiolia"}
                value={indexNamesText}
                onChange={(e) => setIndexNamesText(e.target.value)}
              />
            </div>
            <div className="flex items-start gap-4 px-5 py-3.5">
              <div className="w-64 shrink-0 pt-2">
                <span className="text-[13px] font-medium text-gray-600">IP autorisées</span>
                <span className="block text-xs text-gray-500">Une IP par ligne — vide = accès sans restriction. Les comptes du réseau ne pourront se connecter à l'admin que depuis ces IP (hors superadmin).</span>
              </div>
              <textarea
                className={`${INPUT_CLASS_NAME} min-h-[100px] resize-y font-mono text-xs`}
                placeholder={"178.23.156.132"}
                value={allowedIpsText}
                onChange={(e) => setAllowedIpsText(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddNetwork;
